import React, { CSSProperties, useCallback } from "react";
import {
    CellMeasurerCache,
    List,
    CellMeasurerCacheParams,
    AutoSizer,
    CellMeasurer,
    ListRowProps,
} from "react-virtualized";

const COLUMN_INDEX = 0;

type RowRendererChildren<T extends unknown[]> = ({
    renderProps,
    element,
}: {
    renderProps: ListRowProps;
    element: T[number];
}) => React.ReactElement;

interface VirtualRowCachedListChildProps<T extends unknown[]> {
    children: RowRendererChildren<T>;
    data: T[];
}

const VirtualRowCachedListChild = ({ children, data }: VirtualRowCachedListChildProps<typeof data>) => {
    const { cache, listRef } = useCachedVirtualListContext();
    data = data.filter((value, index, self) => index === self.findIndex((item) => item.id === value.id));
    return (
        <AutoSizer>
            {({ height, width }) => (
                <List
                    rowRenderer={(props) => (
                        <RowRendererResult key={props.key} rendererProps={props} element={data[props.index] ?? null}>
                            {children}
                        </RowRendererResult>
                    )}
                    width={width}
                    height={height}
                    rowCount={data.length}
                    deferredMeasurementCache={cache}
                    rowHeight={cache.rowHeight}
                    ref={listRef}
                />
            )}
        </AutoSizer>
    );
};

interface IRowRendererResult<T extends unknown[]> {
    rendererProps: ListRowProps;
    children: RowRendererChildren<T>;
    element: T;
}
const RowRendererResult = ({ rendererProps, children, element }: IRowRendererResult<typeof element>) => {
    const { index, key, style, parent: cacheParent } = rendererProps;
    const { cache } = useCachedVirtualListContext();
    return (
        <CellMeasurer cache={cache} columnIndex={COLUMN_INDEX} key={key} parent={cacheParent} rowIndex={index}>
            <div style={style}>{children({ renderProps: rendererProps, element })}</div>
        </CellMeasurer>
    );
};

//----------------------------------------------------------------------------//
//====Provider and hooks to create a context used in the Virtual List above===//
//----------------------------------------------------------------------------//

interface VirtualCacheHookProps {
    cacheOptions?: CellMeasurerCacheParams;
}

interface VitualListCacheProviderProps<T extends unknown[]> {
    hookInstance: ReturnType<typeof useVirtualListCache>;
    data: T;
    children: RowRendererChildren<T>;
    customWrapperCSS?: CSSProperties;
}

export const useVirtualListCache = ({ cacheOptions = {} }: VirtualCacheHookProps) => {
    const listRef = React.useRef<List>();
    const cache = React.useMemo(() => new CellMeasurerCache(cacheOptions), [cacheOptions]);

    const recomputeRowHeight = React.useCallback((rowIndex: number) => {
        listRef.current.recomputeRowHeights(rowIndex);
    }, []);

    const clearRowCache = useCallback(
        (rowIndex: number) => {
            cache.clear(rowIndex, COLUMN_INDEX);
        },
        [cache]
    );

    return { listRef, cache, recomputeRowHeight, clearRowCache };
};

const VirtualListCacheContext = React.createContext<ReturnType<typeof useVirtualListCache>>(null);
VirtualListCacheContext.displayName = "VirtualListCacheContext";

export const VirtualCachedRowList = <T extends unknown[]>({
    hookInstance,
    customWrapperCSS,
    ...props
}: VitualListCacheProviderProps<T>) => {
    if (!hookInstance) {
        throw new Error("useVirtualListCache instance must be provided");
    }

    return (
        <div style={{ height: "100%", ...customWrapperCSS }}>
            <VirtualListCacheContext.Provider value={hookInstance}>
                <VirtualRowCachedListChild {...props} />
            </VirtualListCacheContext.Provider>
        </div>
    );
};

const useCachedVirtualListContext = () => React.useContext(VirtualListCacheContext);
